import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { scoreTone, etichettaFor, fmtCheckDate } from "@/lib/constants";
import {
  Sparkles, ArrowUpRight, FileText, ListChecks, CalendarClock, Euro, ShieldCheck, CheckCircle2, Globe, HelpCircle, Ban, Clock,
} from "lucide-react";

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-500">
        <Icon className="h-4 w-4 text-indigo-500" />
        {title}
      </h4>
      <div className="mt-2 text-sm text-slate-700">{children}</div>
    </div>
  );
}

export function GrantDetailsModal({ grant, open, onOpenChange }) {
  if (!grant) return null;
  const tone = scoreTone(grant.compatibilita || 0);
  const eti = etichettaFor(grant);
  const checkDate = fmtCheckDate(grant.ultimo_controllo);
  const confermati = grant.requisiti_confermati || grant.criteri_compatibili || [];
  const daVerificare = grant.requisiti_da_verificare || [];
  const esclusioni = grant.esclusioni || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="grant-details-modal">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${eti.cls}`}>{eti.label}</span>
            <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">{grant.ente}</span>
            <span className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${tone.cls}`}>
              {grant.compatibilita}% compatibilità
            </span>
            {checkDate && (
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-500">
                <Clock className="h-3 w-3" /> Controllato il {checkDate}
              </span>
            )}
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-slate-900">{grant.titolo}</DialogTitle>
          <DialogDescription className="text-slate-500">{grant.sottotitolo}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {grant.motivazione_match && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm italic text-slate-600" data-testid="grant-match-basis">
              Su cosa si basa: {grant.motivazione_match}
            </p>
          )}
          {(confermati.length > 0 || daVerificare.length > 0) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3">
                <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700">
                  <ShieldCheck className="h-4 w-4" /> Requisiti confermati
                </h4>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {confermati.length ? confermati.map((c, i) => (
                    <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{c}</li>
                  )) : <li className="text-slate-400">Da verificare</li>}
                </ul>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3">
                <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-700">
                  <HelpCircle className="h-4 w-4" /> Da verificare
                </h4>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {daVerificare.length ? daVerificare.map((c, i) => (
                    <li key={i} className="flex items-start gap-1.5"><HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />{c}</li>
                  )) : <li className="text-slate-400">Nessun elemento segnalato</li>}
                </ul>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3">
            <div className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Importo</div>
                <div className="text-sm font-semibold text-slate-800">{grant.importo_max}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Copertura</div>
                <div className="text-sm font-semibold text-slate-800">{grant.copertura}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-amber-500" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Scadenza</div>
                <div className="text-sm font-semibold text-slate-800">{grant.scadenza}</div>
              </div>
            </div>
          </div>

          {grant.analisi_ia && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
              <h4 className="flex items-center gap-2 font-display text-sm font-bold text-indigo-700">
                <Sparkles className="h-4 w-4" />
                Analisi di Compatibilità IA
              </h4>
              <p className="mt-2 text-sm text-slate-700">{grant.analisi_ia}</p>
              {Array.isArray(grant.criteri_compatibili) && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {grant.criteri_compatibili.map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                      <CheckCircle2 className="h-3 w-3" />{c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {Array.isArray(grant.spese_ammissibili) && grant.spese_ammissibili.length > 0 && (
            <Section icon={FileText} title="Spese ammissibili">
              <ul className="list-disc space-y-1 pl-5">
                {grant.spese_ammissibili.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}

          {esclusioni.length > 0 && (
            <Section icon={Ban} title="Esclusioni">
              <ul className="list-disc space-y-1 pl-5 text-slate-600">
                {esclusioni.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}

          {Array.isArray(grant.documentazione) && grant.documentazione.length > 0 && (
            <Section icon={ListChecks} title="Documentazione richiesta">
              <ul className="space-y-1">
                {grant.documentazione.map((d, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{d}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {Array.isArray(grant.timeline) && grant.timeline.length > 0 && (
            <Section icon={CalendarClock} title="Timeline domanda">
              <ol className="space-y-2">
                {grant.timeline.map((t, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">{i + 1}</span>
                    {t}
                  </li>
                ))}
              </ol>
            </Section>
          )}
        </div>

        {Array.isArray(grant.fonti) && grant.fonti.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="grant-sources">
            <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-500">
              <Globe className="h-4 w-4 text-indigo-500" />
              Fonti web verificate
            </h4>
            <ul className="mt-2 space-y-1.5">
              {grant.fonti.map((f, i) => (
                <li key={i}>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="grant-source-link"
                    className="inline-flex items-start gap-1.5 text-sm text-indigo-600 hover:underline"
                  >
                    <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{f.titolo || f.url}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {grant.link_ufficiale && (
          <Button asChild className="mt-2 w-full gap-2" data-testid="grant-official-link-button">
            <a href={grant.link_ufficiale} target="_blank" rel="noopener noreferrer">
              Vai al portale ufficiale del bando
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
